import { LogIn } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import reachLogo from '../assets/logo-gradient-white-text.svg';
import { useAuth } from '../hooks/useAuth';

export const LoginPage: React.FC = () => {
  const { login, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }

    // Check for error from backend redirect
    const errorParam = searchParams.get('error');
    if (errorParam) {
      const errorMessages: Record<string, string> = {
        no_code: 'No authorization code provided',
        auth_failed: 'Authentication failed. Please try again.',
      };
      setError(errorMessages[errorParam] || 'An error occurred during sign in');

      // Clean up URL by removing error param
      searchParams.delete('error');
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  const handleLogin = async () => {
    try {
      setError(null);
      // Login without organizationId - we'll auto-select first org after login
      await login();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to login');
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-slate-900 p-4 ">
      <div className="backdrop-invisible absolute left-[15%] top-[15%] h-[720px] w-[1057px] rotate-[15deg] rounded-full opacity-60 blur-[300px] sm:visible bg-gradient-to-br from-slate-900 via-slate-200 to-slate pointer-events-none" />
      <div className="max-w-md w-full space-y-8">
        {/* Login Card */}
        <div className="bg-white/15 border-2 border-white/20 backdrop-blur-2xl rounded-2xl shadow-2xl p-8 space-y-6">
          <div className="space-y-4">
            <div className="flex justify-center">
              <img src={reachLogo} alt="Reach Logo" className="h-12 w-auto" />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full flex justify-center items-center space-x-2 py-3.5 px-4 bg-black hover:bg-gray-800 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm">
            <LogIn className="h-5 w-5" />
            <span>{isLoading ? 'Signing in...' : 'Sign in'}</span>
          </button>

          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-white/70 text-center leading-relaxed">
              By signing in, you agree to our{' '}
              <a
                href="https://www.embedreach.com/terms-of-service/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white">
                terms of service
              </a>{' '}
              and{' '}
              <a
                href="https://www.embedreach.com/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white">
                privacy policy
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-slate-500 text-xs">
            © 2025 Embed Reach. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};
